import React, { useState } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from "@material-ui/core/styles";
import { Divider, Typography } from '@material-ui/core';
import { client } from "../../utils/data/graphql/client";
import { completeOtcOrderMutation } from "../../utils/data/graphql/otc/mutations";
import ActionButton from '../button/actionButton';
import TextField from '../input/textField';
import { useAuth0 } from "@auth0/auth0-react";

const useStyles = makeStyles((theme) => ({
    root: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        width: '416px',
        minHeight: '540px',
        maxHeight: '90vh',
        borderRadius: '20px',
        background: theme.background.dialog,
        overflowY: 'auto',
    },
    cardContent: {
        margin: '32px !important',
        padding: '0 !important',
        display: 'flex',
        justifyContent: 'center',
    },
    backdrop: {
        ['@supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none))']: {
            '-webkit-backdrop-filter': 'blur(2em)',
            backdropFilter: 'blur(2em)',
            backgroundColor: 'rgb(0 0 0 / 33%) !important',
        }
    },
    content: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '42px',
    },
    closeButton: {
        position: 'absolute',
        top: theme.spacing(4),
        right: theme.spacing(3),
    },
    heading: {
        marginTop: '12px',
        alignSelf: 'start'
    },
    readOnlySection: {
        width: '100%',
        padding: '16px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '8px',
        marginTop: '16px',
    },
    amountRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '12px',
        gap: '16px',
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '16px 0px'
    },
    input: {
        margin: '12px 0px 12px 0px !important',
    },
    selectLabel: {
        marginBottom: '4px',
        marginTop: '8px',
    },
    confirmButton: {
        marginBottom: '40px',
        position: 'relative',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        marginTop: '24px',
        justifyContent: 'flex-end',
        width: '100%',
    },
}));

// Confirmation modal for completing OTC send orders
// Shows read-only transaction details from creation
// For stablecoin sends: collects optional blockchain explorer URL
const CompleteOtcSendDialog = ({ onClose, onComplete, orderId, orderDetails = {} }) => {
    const classes = useStyles();
    const { user } = useAuth0();
    const [submitting, setSubmitting] = useState(false);
    
    // Read-only transaction details from creation
    const {
        customerId = '',
        sourceCurrency = 'USD',
        sourceAmount = 0,
        payoutCurrency = 'USD',
        payoutAmount = 0,
        fxRate = 1
    } = orderDetails;
    
    // Detect if this is a stablecoin send
    const isStablecoinSend = ['USDT', 'USDC', 'BUSD', 'DAI'].includes(payoutCurrency?.toUpperCase());
    
    // Optional blockchain explorer URL (only for stablecoin sends)
    const [blockchainExplorerUrl, setBlockchainExplorerUrl] = useState('');
    const [notes, setNotes] = useState('');

    const handleComplete = async () => {
        setSubmitting(true);
        try {
            const settlementDetails = {};
            
            // Add blockchain explorer URL if provided for stablecoin sends
            if (isStablecoinSend && blockchainExplorerUrl) {
                settlementDetails.transactionUrl = blockchainExplorerUrl;
            }
            
            // Include notes if provided
            if (notes.trim()) {
                settlementDetails.notes = notes.trim();
            }
            
            const input = {
                createdBy: user?.email,
                settlementDetails: Object.keys(settlementDetails).length > 0 ? settlementDetails : undefined
            };
            
            await client.mutate({
                mutation: completeOtcOrderMutation,
                variables: {
                    id: orderId,
                    input
                }
            });
            onClose && onClose();
            onComplete && onComplete(true);
        } catch (e) {
            console.error(e);
            onClose && onClose();
            onComplete && onComplete(false);
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Modal open={true} onClose={onClose} BackdropProps={{className:classes.backdrop}}>
            <Fade in={true}>
                <Card className={classes.root}>
                    <CardContent className={classes.cardContent}>
                        <ActionButton variant="secondary" size="lg"
                                      className={classes.closeButton}
                                      onClick={onClose}
                        >
                            Close
                        </ActionButton>
                        <div className={classes.content}>
                            <Typography variant="h3" className={classes.heading}>Complete OTC Send</Typography>
                            <Typography variant="body1" style={{ alignSelf: 'start', marginTop: '8px' }}>
                                Review transaction details and confirm completion.
                            </Typography>
                            
                            {/* READ-ONLY TRANSACTION DETAILS */}
                            <div className={classes.readOnlySection}>
                                <Typography variant="overline" style={{fontSize: '0.75rem', fontWeight: 600}}>
                                    Transaction Details
                                </Typography>
                                
                                <Typography variant="body2" style={{marginTop: '8px', opacity: 0.7}}>
                                    Customer: {customerId}
                                </Typography>
                                
                                <div className={classes.amountRow}>
                                    <div>
                                        <Typography variant="caption" style={{opacity: 0.7}}>
                                            Source (Deducted)
                                        </Typography>
                                        <Typography variant="h5">
                                            {sourceAmount} {sourceCurrency}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="caption" style={{opacity: 0.7}}>
                                            Payout (Received)
                                        </Typography>
                                        <Typography variant="h5">
                                            {payoutAmount} {payoutCurrency}
                                        </Typography>
                                    </div>
                                </div>
                                
                                <Typography variant="body2" style={{marginTop: '8px', opacity: 0.7}}>
                                    Quote Rate: {fxRate} (1 {sourceCurrency} = {fxRate} {payoutCurrency})
                                </Typography>
                            </div>
                            
                            {/* Optional blockchain explorer URL for stablecoin sends */}
                            {isStablecoinSend && (
                                <>
                                    <Divider style={{width: '100%', margin: '24px 0'}} />
                                    <div style={{width: '100%'}}>
                                        <Typography variant="overline" style={{fontSize: '0.75rem', fontWeight: 600}}>
                                            Blockchain Details (Optional)
                                        </Typography>
                                        <div className={classes.inputContainer}>
                                            <TextField
                                                className={classes.input}
                                                name='blockchainExplorerUrl'
                                                type='text'
                                                value={blockchainExplorerUrl}
                                                label='Blockchain Explorer URL'
                                                placeholder="https://etherscan.io/tx/0x..."
                                                InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                                onChange={(e) => setBlockchainExplorerUrl(e.target.value)}
                                                fullWidth
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            
                            {/* Admin Notes Section */}
                            <Divider style={{width: '100%', margin: '24px 0'}} />
                            <div style={{width: '100%'}}>
                                <Typography variant="overline" style={{fontSize: '0.75rem', fontWeight: 600}}>
                                    Admin Notes (Optional)
                                </Typography>
                                <div className={classes.inputContainer}>
                                    <TextField
                                        className={classes.input}
                                        name='notes'
                                        type='text'
                                        value={notes}
                                        label='Notes'
                                        placeholder="Any additional information..."
                                        InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                        onChange={(e) => setNotes(e.target.value)}
                                        multiline
                                        rows={3}
                                        fullWidth
                                    />
                                </div>
                            </div>
                            
                            <div className={classes.actions}>
                                <ActionButton 
                                    variant="secondary"
                                    onClick={onClose}
                                    disabled={submitting}
                                >
                                    Cancel
                                </ActionButton>
                                <ActionButton 
                                    variant="primary"
                                    onClick={handleComplete}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Completing...' : 'Complete'}
                                </ActionButton>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </Fade>
        </Modal>
    )
}

export default CompleteOtcSendDialog;


